import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Opens right after a Reels MP4 finishes rendering.
 * - Preview the MP4
 * - Share to WhatsApp via Web Share API (with file, on mobile) OR whatsapp://send with a pre-filled message + wall link (fallback)
 * - Download the MP4
 * - Copy the public wall link
 */
export function ReelsShareDialog({ open, onOpenChange, reel, wallUrl, businessName }) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const shareText = useMemo(() => {
    const author = reel?.authorName ? ` de ${reel.authorName}` : "";
    return `Olha esse depoimento${author} sobre ${businessName || "a gente"} 💛\n\n${wallUrl || ""}`;
  }, [reel, businessName, wallUrl]);

  const download = () => {
    if (!reel?.url) return;
    const a = document.createElement("a");
    a.href = reel.url;
    a.download = reel.filename || "reel.mp4";
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("MP4 baixado!");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(wallUrl || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Link do mural copiado!");
    } catch {
      toast.error("Não consegui copiar. Selecione manualmente.");
    }
  };

  const shareWhatsApp = async () => {
    if (!reel?.blob) return;
    setSharing(true);
    try {
      // Try the Web Share API with a File (works on iOS Safari + Android Chrome)
      const file = new File([reel.blob], reel.filename || "reel.mp4", { type: "video/mp4" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Depoimento — ${businessName || "depoia"}`,
          text: shareText,
        });
        toast.success("Compartilhado!");
        return;
      }
      // Fallback: open WhatsApp with pre-filled text (link only, no file — user attaches the downloaded MP4)
      download();
      setTimeout(() => {
        const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(url, "_blank", "noopener");
        toast.info("Abrimos o WhatsApp — anexa o MP4 baixado no chat.");
      }, 250);
    } catch (e) {
      // User canceled — no error to show
      if (e?.name !== "AbortError") toast.error("Não deu pra compartilhar.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reels-share-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reels pronto 🎬</DialogTitle>
          <DialogDescription>
            Compartilha no WhatsApp com 1 tap ou baixa o MP4 pra postar no Instagram/TikTok.
          </DialogDescription>
        </DialogHeader>

        {reel?.url && (
          <div className="aspect-[9/16] max-h-[520px] mx-auto rounded-lg overflow-hidden bg-black">
            <video
              data-testid="reels-share-preview"
              src={reel.url}
              controls
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-contain"
            />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <button
            data-testid="reels-share-whatsapp"
            onClick={shareWhatsApp}
            disabled={sharing}
            className="w-full py-3.5 bg-[#25D366] hover:bg-[#20BA57] text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {sharing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            )}
            Enviar no WhatsApp
          </button>

          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="reels-share-download"
              onClick={download}
              className="py-3 bg-zinc-900 hover:bg-zinc-800 text-white rounded-full font-semibold flex items-center justify-center gap-2 text-sm transition-colors"
            >
              <Download size={14} /> Baixar MP4
            </button>
            <button
              data-testid="reels-share-copy-link"
              onClick={copyLink}
              className="py-3 border border-zinc-300 hover:border-zinc-500 rounded-full font-semibold flex items-center justify-center gap-2 text-sm transition-colors text-zinc-800"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copiado" : "Copiar link"}
            </button>
          </div>

          <p className="text-[11px] text-zinc-400 text-center mt-1">
            No iPhone/Android o compartilhamento anexa o vídeo direto. No desktop, o MP4 é baixado e o WhatsApp Web abre com a mensagem.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
